import { createContext, useContext, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Flags, ScopedBoolean } from '@/api';
import { ConditionFormData } from '@/models/article';
import { type Brand, useBrands } from '@/models/brand';
import { Medium, useMediaQueries } from '@/models/medium';
import { ProductAttribute, ProductAttributesByArticle } from '@/models/productAttribute';
import {
  ArticleWithRelations,
  getRequestorType,
  RequestWithRelations,
  useDepositOptions,
} from '@/models/request';
import { UserFromGetSession, useUserSettings } from '@/models/user';
import {
  getScopedBoolean as getWorkflowScopedBoolean,
  hasOnlinePaymentStep,
  WorkflowPublicWithRelations,
} from '@/models/workflow';
import { useCurrentSession, useFlags } from '@/services/auth';

import { computeCreationContext } from './steps/creation';
import { computeValidationContext } from './steps/validation';
import { computeAcceptDispatchContext } from './tasks/accept_dispatch';
import { computeAcceptDispatchProposalContext } from './tasks/accept_dispatch_proposal';
import { computeAcceptRequalificationContext } from './tasks/accept_requalification';
import { computeAdjustServiceDuringProcessingContext } from './tasks/adjust_service_during_processing';
import { computeAdjustServiceDuringQualityControlContext } from './tasks/adjust_service_during_quality_control';
import { computeAnalyzeArticleContext } from './tasks/analyze_article';
import { computeChooseArticleServiceContext } from './tasks/choose_article_service';
import { computeControlQualityContext } from './tasks/control_quality';
import { computeExchangeArticleContext } from './tasks/exchange_article';
import { computeRepairArticleContext } from './tasks/repair_article';
import { computeStartArticleExchangeContext } from './tasks/start_article_exchange';
import { computeStartArticleRepairContext } from './tasks/start_article_repair';
import { computeRequestErrors, recursivelyFilterErrors, RequestErrorsFilter } from './errors';
import { getRequestIam } from './iam';
import { RequestArticleContextData, RequestContextData } from './interfaces';

export const RequestContext = createContext({} as RequestContextData);

export const RequestContextProvider = ({
  request,
  workflow,
  initialConditionFormsData,
  qualityControlFormsData,
  productAttributesData,
  children,
}: {
  request: RequestWithRelations;
  workflow: WorkflowPublicWithRelations | undefined;
  initialConditionFormsData?: { articleId: string; formData?: ConditionFormData }[];
  qualityControlFormsData?: { articleId: string; formData?: ConditionFormData }[];
  productAttributesData?: ProductAttributesByArticle[];
  children: React.ReactNode;
}) => {
  const value = useComputedRequestContext({
    request,
    workflow,
    initialConditionFormsData,
    qualityControlFormsData,
    productAttributesData,
  });

  return <RequestContext value={value}>{children}</RequestContext>;
};

export const useRequestContext = () => useContext(RequestContext);

function useComputedRequestContext({
  request,
  workflow,
  initialConditionFormsData,
  qualityControlFormsData,
  productAttributesData,
}: {
  request: RequestWithRelations;
  workflow: WorkflowPublicWithRelations | undefined;
  initialConditionFormsData?: { articleId: string; formData?: ConditionFormData }[];
  qualityControlFormsData?: { articleId: string; formData?: ConditionFormData }[];
  productAttributesData?: ProductAttributesByArticle[];
}) {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const { flags } = useFlags();
  const { data: userSettings } = useUserSettings(currentSession!.id);
  const { data: { brands: organizationBrands } = { brands: [] } } = useBrands({
    organizationId: request.organizationId,
    limit: 500,
    offset: 0,
  });
  const iam = getRequestIam(request);

  // Each query is done independently in order to match the query key
  // from the query done on the polling of the FileUpload component
  const proofOfPurchaseQueries = useMediaQueries(
    request.allArticles.map((article) => ({
      articleId: article.id,
      types: ['proof-of-purchase'],
    }))
  );
  const proofOfAuthenticityQueries = useMediaQueries(
    request.allArticles.map((article) => ({
      articleId: article.id,
      types: ['proof-of-authenticity'],
    }))
  );
  const proofOfPurchaseMedia = proofOfPurchaseQueries.flatMap((query) => query.data?.media ?? []);
  const proofOfAuthenticityMedia = proofOfAuthenticityQueries.flatMap(
    (query) => query.data?.media ?? []
  );

  const getScopedBoolean = (scopedBoolean: ScopedBoolean | undefined) => {
    return getWorkflowScopedBoolean({
      scopedBoolean,
      credentialsScopes: request.scopes,
    });
  };

  const creationStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'creation');
  const isDropOffSignatureEnabled = creationStep?.config?.dropOffSignature?.enabled ?? false;

  const isPickupSignatureEnabled = !!workflow?.nodes.find(
    (node) =>
      'step' in node && node.step === 'transit' && node.config.clientPickUpSignature?.enabled
  );

  const userWorkshopId = iam.scopesOnRequest.find(({ scope }) => scope === 'workshop')?.entityId;
  const userWorkshopOrganization = request.allArticles.find(
    (article) => article.workshopId === userWorkshopId
  )?.workshopOrganization;

  const isWorkshopThatCannotSeeOrganizationPrice =
    iam.hasOnlyWorkshopScopeOnRequest &&
    !userWorkshopOrganization?.workshopConfig?.organizationPrice?.shown;

  const showWorkshopPriceAmountBeforeTaxes =
    userSettings?.priceDisplay?.workshopPrice.showAmountBeforeTaxes ??
    iam.hasOnlyWorkshopScopeOnRequest;
  const showOrganizationPriceAmountBeforeTaxes =
    userSettings?.priceDisplay?.organizationPrice.showAmountBeforeTaxes ??
    iam.hasOnlyWorkshopScopeOnRequest;

  const workshopPriceLabel = iam.hasOnlyWorkshopScopeOnRequest
    ? t({
        id: 'request-context.price.workshop',
        message: 'Price',
      })
    : t({
        id: 'request-context.cost',
        message: 'Internal cost',
      });

  const organizationPriceLabel = t({
    id: 'request-context.price.organization',
    message: 'Client price',
  });

  const fieldsConfig = workflow?.config.fields?.request;

  const requestView: RequestContextData['view'] = {
    information: {
      salesAdvisor: {
        enabled:
          !!request.store?.storeConfig?.salesAdvisors?.length &&
          getScopedBoolean(fieldsConfig?.information?.salesAdvisor?.enabled),
        required: getScopedBoolean(fieldsConfig?.information?.salesAdvisor?.required),
      },
      externalReference: {
        enabled: getScopedBoolean(fieldsConfig?.information?.externalReference?.enabled),
        required: getScopedBoolean(fieldsConfig?.information?.externalReference?.required),
      },
      customFields: (fieldsConfig?.information?.customFields ?? []).filter(
        (customField) => customField.enabled
      ),
    },
    client: {
      enabled: getScopedBoolean(fieldsConfig?.client?.enabled),
      required: getScopedBoolean(fieldsConfig?.client?.required),
      vip: {
        enabled: getScopedBoolean(fieldsConfig?.client?.vip?.enabled),
      },
      name: {
        editable: getScopedBoolean(fieldsConfig?.client?.name?.editable),
      },
      composedName: {
        enabled: getScopedBoolean(fieldsConfig?.client?.composedName?.enabled),
      },
      address: {
        enabled: getScopedBoolean(fieldsConfig?.client?.address?.enabled),
      },
      billingAddress: {
        enabled: !!workflow && hasOnlinePaymentStep(workflow),
      },
      dropoffSignature: {
        enabled: isDropOffSignatureEnabled,
      },
      pickupSignature: {
        enabled: isPickupSignatureEnabled,
      },
    },
    workshopPrice: {
      shown: false,
      label: `${workshopPriceLabel} (${
        showWorkshopPriceAmountBeforeTaxes
          ? t({ id: '_general.excl-vat', message: 'excl. VAT' })
          : t({ id: '_general.incl-vat', message: 'incl. VAT' })
      })`,
      labelWithoutVATInfo: workshopPriceLabel,
      showAmountBeforeTaxes: showWorkshopPriceAmountBeforeTaxes,
    },
    organizationPrice: {
      shown: false,
      label: `${organizationPriceLabel} (${
        showOrganizationPriceAmountBeforeTaxes
          ? t({ id: '_general.excl-vat', message: 'excl. VAT' })
          : t({ id: '_general.incl-vat', message: 'incl. VAT' })
      })`,
      labelWithoutVATInfo: organizationPriceLabel,
      showAmountBeforeTaxes: showOrganizationPriceAmountBeforeTaxes,
      editable: getScopedBoolean(fieldsConfig?.articles?.prices?.organizationPrice?.editable),
      negativeEnabled: !!fieldsConfig?.articles?.prices?.organizationPrice?.negativeEnabled,
    },
    customs: {
      enabled: request.organization.config.customs?.enabled ?? false,
      type: request.organization.config.customs?.type ?? 'fields',
    },
    items: {
      serviceEligibility: {
        enabled: getScopedBoolean(workflow?.config.serviceEligibility?.enabled),
      },
    },
  };

  const firstActiveArticle = request.articles[0];

  if (firstActiveArticle?.step?.step === 'creation') {
    const creationConfig = firstActiveArticle.step.config;

    requestView.workshopPrice.shown =
      getScopedBoolean(fieldsConfig?.articles?.prices?.workshopPrice?.displayed) &&
      (userSettings?.priceDisplay?.workshopPrice.enabled ?? true) &&
      !!creationConfig?.requireServiceChoice &&
      !!creationConfig?.requireDispatch;
    requestView.organizationPrice.shown =
      !!creationConfig?.requireServiceChoice && getRequestorType(request) !== 'store';
  } else {
    requestView.workshopPrice.shown =
      getScopedBoolean(fieldsConfig?.articles?.prices?.workshopPrice?.displayed) &&
      (userSettings?.priceDisplay?.workshopPrice.enabled ?? true);

    requestView.organizationPrice.shown =
      !isWorkshopThatCannotSeeOrganizationPrice && getRequestorType(request) !== 'store';
  }

  if (firstActiveArticle?.step?.step === 'creation' || !firstActiveArticle) {
    requestView.client.name.editable = true;
  }

  const articles = request.allArticles.map((article) =>
    computeArticleContext({
      request,
      requestView,
      article,
      workflow,
      initialConditionFormData: initialConditionFormsData?.find(
        ({ articleId }) => articleId === article.id
      )?.formData,
      qualityControlFormData: qualityControlFormsData?.find(
        ({ articleId }) => articleId === article.id
      )?.formData,
      currentSession: currentSession!,
      proofOfPurchaseMedia: proofOfPurchaseMedia.filter((media) => media.articleId === article.id),
      proofOfAuthenticityMedia: proofOfAuthenticityMedia.filter(
        (media) => media.articleId === article.id
      ),
      organizationBrands,
      productAttributes:
        productAttributesData?.find(({ articleId }) => articleId === article.id)
          ?.productAttributes ?? [],
      flags,
      iam,
    })
  );

  const [errorsFilter, setErrorsFilter] = useState<RequestErrorsFilter | null>(null);
  // We only need to fetch the deposit options if the request has at least one article being created
  const { data: depositOptions } = useDepositOptions({
    requestId: request.id,
    enabled: request.articles.some((article) => article.step?.step === 'creation'),
  });

  const unfilteredErrors = computeRequestErrors({
    request,
    currentSession: currentSession!,
    proofOfPurchaseMedia,
    proofOfAuthenticityMedia,
    initialConditionFormsData,
    qualityControlFormsData,
    productAttributesData,
    requestView,
    depositOptions,
  });

  const errors = recursivelyFilterErrors(unfilteredErrors, errorsFilter ?? {});

  return {
    request,
    workflow,
    view: requestView,
    articles,
    unfilteredErrors,
    errors,
    hasError: errors.hasError ?? false,
    state: {
      errors: {
        check(errorsFilter: RequestErrorsFilter | null) {
          setErrorsFilter(errorsFilter);

          // Directly returns the filtered errors, as the context hasn't re-rendered yet, so the state is not updated
          return recursivelyFilterErrors(unfilteredErrors, errorsFilter ?? {});
        },
      },
    },
    iam,
  };
}

function computeArticleContext({
  request,
  requestView,
  workflow,
  article,
  initialConditionFormData,
  qualityControlFormData,
  currentSession,
  proofOfPurchaseMedia,
  proofOfAuthenticityMedia,
  organizationBrands,
  productAttributes,
  flags,
  iam,
}: {
  request: RequestWithRelations;
  requestView: RequestContextData['view'];
  workflow: WorkflowPublicWithRelations | undefined;
  article: ArticleWithRelations;
  initialConditionFormData: ConditionFormData | undefined;
  qualityControlFormData: ConditionFormData | undefined;
  currentSession: UserFromGetSession;
  proofOfPurchaseMedia: Medium[];
  proofOfAuthenticityMedia: Medium[];
  organizationBrands: Brand[];
  productAttributes: ProductAttribute[];
  flags: Flags;
  iam: ReturnType<typeof getRequestIam>;
}) {
  const getScopedBoolean = (scopedBoolean: ScopedBoolean | undefined) => {
    return getWorkflowScopedBoolean({
      scopedBoolean,
      credentialsScopes: request.scopes,
    });
  };

  const fieldsConfig = workflow?.config.fields?.request?.articles;

  const warrantyType = fieldsConfig?.warranty?.type ?? 'warranty';

  /**
   * Makes a field required if the warranty is applicable and if the field config mentions it as required
   */
  const enableWarrantyFieldsRequiredConfig =
    warrantyType === 'warranty' ? !!article.activeWarrantyType : true;

  const workshopBeforeActionsEnabled = getScopedBoolean(
    workflow?.config.workshopBeforeActions?.enabled
  );

  // Brand responsibility is also enabled out of warranty, to allow exceptional coverage
  // and explicit activation by the brand (the default brand responsibility is then ignored)
  const brandResponsibilityEnabled =
    !!request.client &&
    getScopedBoolean(fieldsConfig?.warranty?.enabled) &&
    warrantyType === 'warranty';

  const defectsEnabled = getScopedBoolean(fieldsConfig?.defects?.enabled);

  // 1. This represents the "base" view
  const view: RequestArticleContextData['view'] = {
    details: {
      serialNumber: {
        shown: getScopedBoolean(fieldsConfig?.details?.serialNumber?.enabled),
        required:
          getScopedBoolean(fieldsConfig?.details?.serialNumber?.enabled) &&
          !!article.product?.serialNumberEnabled &&
          !!fieldsConfig?.details?.serialNumber?.requiredAtStep?.creation,
        history: {
          shown: getScopedBoolean(fieldsConfig?.requestHistory?.enabled) && !!article.uniqueArticle,
        },
        canCreate: getScopedBoolean(fieldsConfig?.details?.serialNumber?.addSerialNumber?.enabled),
      },
      product: {
        canCreate: getScopedBoolean(
          fieldsConfig?.details?.itemReference?.addProductReference?.enabled
        ),
      },
      color: {
        shown: getScopedBoolean(fieldsConfig?.details?.color?.enabled),
        required: getScopedBoolean(fieldsConfig?.details?.color?.required),
      },
      size: {
        shown: getScopedBoolean(fieldsConfig?.details?.size?.enabled),
        required: getScopedBoolean(fieldsConfig?.details?.size?.required),
      },
      weight: {
        shown: getScopedBoolean(fieldsConfig?.details?.weight?.enabled),
        required: getScopedBoolean(fieldsConfig?.details?.weight?.required),
      },
      brand: {
        shown:
          !!organizationBrands.length && getScopedBoolean(fieldsConfig?.details?.brand?.enabled),
        required: getScopedBoolean(fieldsConfig?.details?.brand?.required),
      },
      articlePhoto: {
        shown: getScopedBoolean(fieldsConfig?.details?.photo?.enabled),
        required: getScopedBoolean(fieldsConfig?.details?.photo?.required),
      },
      estimatedValue: {
        shown: getScopedBoolean(fieldsConfig?.details?.estimatedValue?.enabled),
        required: getScopedBoolean(fieldsConfig?.details?.estimatedValue?.required),
      },
    },
    initialCondition: {
      shown: getScopedBoolean(fieldsConfig?.initialCondition?.enabled),
      required: getScopedBoolean(fieldsConfig?.initialCondition?.required),
      editable: false,
      photo: {
        shown: true,
        required: false,
      },
      context: {
        shown: true,
        required: false,
      },
    },
    qualityControl: {
      shown:
        !!workflow?.conditionForms?.find(({ type }) => type === 'quality_control') &&
        !!article.steps?.find(
          ({ step, status }) =>
            step === 'quality-control' && ['done', 'in-progress', 'cancelled'].includes(status)
        ),
      editable: false,
      photos: {
        required: false,
      },
    },
    warranty: {
      shown: !!request.client && getScopedBoolean(fieldsConfig?.warranty?.enabled),
      type: warrantyType,
      editable: false,
      fields: {
        purchaseDate: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.purchaseDate?.enabled),
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.purchaseDate?.required),
        },
        purchaseLocation: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.purchaseLocation?.enabled),
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.purchaseLocation?.required),
        },
        purchaseCountry: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.purchaseCountry?.enabled),
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.purchaseCountry?.required),
        },
        purchaseChannel: {
          shown:
            getScopedBoolean(fieldsConfig?.warranty?.purchaseChannel?.enabled) &&
            !!fieldsConfig?.warranty?.purchaseChannel?.options.length,
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.purchaseChannel?.required),
          options: fieldsConfig?.warranty?.purchaseChannel?.options ?? [],
        },
        orderNumber: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.orderNumber?.enabled),
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.orderNumber?.required),
        },
        proofOfPurchase: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.proofOfPurchase?.enabled),
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.proofOfPurchase?.required),
        },
        proofOfPurchaseOrOrderNumber: {
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.proofOfPurchaseOrOrderNumber?.required),
        },
        proofOfAuthenticity: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.proofOfAuthenticity?.enabled),
          required:
            enableWarrantyFieldsRequiredConfig &&
            getScopedBoolean(fieldsConfig?.warranty?.proofOfAuthenticity?.required),
        },
        authenticityCertificate: {
          shown: getScopedBoolean(fieldsConfig?.warranty?.authenticityCertificate?.enabled),
          activationDate: {
            shown: getScopedBoolean(
              fieldsConfig?.warranty?.authenticityCertificate?.activationDate?.enabled
            ),
          },
          activationLocation: {
            shown: getScopedBoolean(
              fieldsConfig?.warranty?.authenticityCertificate?.activationLocation?.enabled
            ),
          },
        },
        warrantyOverrideReason: {
          required: enableWarrantyFieldsRequiredConfig,
        },
      },
    },
    defects: {
      shown: defectsEnabled,
      required: getScopedBoolean(fieldsConfig?.defects?.required),
      canAddRemoveDefect: false,
      customEnabled: getScopedBoolean(fieldsConfig?.defects?.custom?.enabled),
      fields: {
        description: {
          editable: false,
        },
        photo: {
          shown: getScopedBoolean(fieldsConfig?.defects?.photo?.enabled),
          required: getScopedBoolean(fieldsConfig?.defects?.photo?.required),
          editable: false,
        },
        context: {
          shown: getScopedBoolean(fieldsConfig?.defects?.context?.enabled),
          required: getScopedBoolean(fieldsConfig?.defects?.context?.required),
          editable: false,
        },
        toBeRepaired: {
          shown: getScopedBoolean(fieldsConfig?.defects?.toBeRepaired?.enabled),
          editable: false,
        },
        brandResponsibility: {
          enabled: brandResponsibilityEnabled,
          toggle: {
            shown: getScopedBoolean(fieldsConfig?.defects?.brandResponsibility?.editable),
            editable: false,
          },
          exceptionalCoverage: {
            enabled: getScopedBoolean(
              fieldsConfig?.defects?.brandResponsibility?.exceptionalCoverage?.enabled
            ),
            toggle: {
              editable: false,
            },
          },
        },
      },
    },
    services: {
      shown: false, // Computed field
      choice: {
        shown: false,
        editable: false,
      },
      dispatchBeforeActions: workshopBeforeActionsEnabled,
      actions: {
        shown:
          article.snapshot.serviceChoice === 'care-repair' &&
          (workshopBeforeActionsEnabled ? !!article.workshopId : true),
        canAddAction: false,
        canRemoveAction: false,
        customEnabled: getScopedBoolean(fieldsConfig?.actions?.custom?.enabled),
        fields: {
          description: {
            editable: false,
          },
          photo: {
            shown: getScopedBoolean(fieldsConfig?.actions?.photo?.enabled),
            required: getScopedBoolean(fieldsConfig?.actions?.photo?.required),
            editable: false,
          },
          context: {
            shown: getScopedBoolean(fieldsConfig?.actions?.context?.enabled),
            required: getScopedBoolean(fieldsConfig?.actions?.context?.required),
            editable: false,
          },
          associatedDefects: {
            shown: getScopedBoolean(fieldsConfig?.defects?.enabled),
            editable: false,
          },
          additionalInfo: {
            editable: false,
          },
          brandResponsibility: {
            shown:
              brandResponsibilityEnabled &&
              !defectsEnabled &&
              !iam.hasOnlyExternalWorkshopScopeOnRequest,
            editable: false,
          },
          workshopPrice: {
            editable: false,
            required: false,
          },
          organizationPrice: {
            editable: false,
            required: false,
          },
          spareParts: {
            shown: getScopedBoolean(fieldsConfig?.actions?.spareParts?.enabled),
            canAddRemoveSparePart: false,
            referenceNumber: {
              shown: getScopedBoolean(fieldsConfig?.actions?.spareParts?.referenceNumber?.enabled),
              editable: false,
            },
            quantity: {
              editable: false,
            },
            prices: {
              shown: getScopedBoolean(fieldsConfig?.actions?.spareParts?.prices?.enabled),
            },
            workshopPrice: {
              required: false,
              editable: false,
            },
            organizationPrice: {
              required: false,
              editable: false,
            },
          },
        },
      },
      dispatch: {
        shown:
          !iam.hasOnlyWorkshopScopeOnRequest && article.snapshot.serviceChoice === 'care-repair',
        editable: false,
      },
      none: {
        shown: article.snapshot.serviceChoice === 'none',
      },
      exchange: {
        shown: article.snapshot.serviceChoice === 'exchange',
        exchangeType: {
          editable: false,
        },
        replacementProduct: {
          editable: false,
        },
        replacementUniqueArticle: {
          shown:
            getScopedBoolean(fieldsConfig?.details?.serialNumber?.enabled) &&
            !!article.snapshot.articleExchange?.replacementProduct?.serialNumberEnabled,
          editable: false,
          required:
            getScopedBoolean(fieldsConfig?.details?.serialNumber?.enabled) &&
            !!article.snapshot.articleExchange?.replacementProduct?.serialNumberEnabled &&
            !!fieldsConfig?.details?.serialNumber?.requiredAtStep?.exchange,
        },
        workshopPrice: {
          editable: false,
          required: false,
        },
        organizationPrice: {
          editable: false,
          required: false,
        },
        stockLocation: {
          editable: false,
        },
      },
      exchangeArchival: {
        shown: article.snapshot.serviceChoice === 'exchange-archival',
      },
      refund: {
        shown: article.snapshot.serviceChoice === 'refund',
      },
    },
    prices: {
      thresholds: {
        client: {
          shown:
            !!request.client &&
            getScopedBoolean(fieldsConfig?.prices?.clientThreshold?.enabled) &&
            !!article.steps?.some((step) => step.step === 'validation'),
          editable: false,
        },
        organization: {
          shown:
            getScopedBoolean(fieldsConfig?.prices?.organizationThreshold?.enabled) &&
            !!article.steps?.some((step) => ['analysis', 'processing'].includes(step.step)),
          editable: false,
        },
      },
      discount: {
        client: {
          shown: getScopedBoolean(fieldsConfig?.prices?.clientDiscount?.enabled),
        },
      },
    },
    itemSheet: {
      preview: {
        enabled:
          article.taskType === 'analyze_article' &&
          getScopedBoolean(workflow?.config.itemSheetPreview?.enabled),
      },
    },
  };

  // 2. We edit the base view, depending on the step if the article is not archived
  if (!article.archivedAt) {
    const computeArticleContextFromSteps = {
      creation: computeCreationContext,
      validation: computeValidationContext,
    };

    computeArticleContextFromSteps[
      article.step?.step as keyof typeof computeArticleContextFromSteps
    ]?.({
      request,
      requestView,
      workflow,
      // @ts-ignore --- Trust me, I'm an engineer
      article,
      view,
      currentSession,
      flags,
    });
  }

  const computeArticleContextFromTasks = {
    accept_dispatch: computeAcceptDispatchContext,
    accept_requalification: computeAcceptRequalificationContext,
    accept_dispatch_proposal: computeAcceptDispatchProposalContext,
    adjust_service_during_processing: computeAdjustServiceDuringProcessingContext,
    adjust_service_during_quality_control: computeAdjustServiceDuringQualityControlContext,
    analyze_article: computeAnalyzeArticleContext,
    choose_article_service: computeChooseArticleServiceContext,
    control_quality: computeControlQualityContext,
    start_article_exchange: computeStartArticleExchangeContext,
    start_article_repair: computeStartArticleRepairContext,
    repair_article: computeRepairArticleContext,
    exchange_article: computeExchangeArticleContext,
  };

  // 3. We edit the base+step view, depending on the task
  computeArticleContextFromTasks[article.taskType as keyof typeof computeArticleContextFromTasks]?.(
    {
      request,
      requestView,
      workflow,
      article,
      view,
      currentSession,
    }
  );

  // 4. Compute computed fields
  computeComputedField(view);

  return {
    articleId: article.id,
    view,
    proofOfPurchaseMedia,
    proofOfAuthenticityMedia,
    initialConditionFormData,
    qualityControlFormData,
    productAttributes,
  };
}

function computeComputedField(view: RequestArticleContextData['view']) {
  view.services.shown =
    view.services.choice.shown ||
    view.services.actions.shown ||
    view.services.dispatch.shown ||
    view.services.exchange.shown ||
    view.services.exchangeArchival.shown ||
    view.services.refund.shown ||
    view.services.none.shown;
}
